import { useEffect, useState } from "react";
import axios from "axios";
import { Filter, Play, ExternalLink } from "lucide-react";
import { EPISODES as FALLBACK_EPISODES } from "../lib/episodes";
import { ETV_WIN_SERIES_URL } from "../lib/logos";
import { EpisodeReel } from "../components/EpisodeReel";
import { ReelModal } from "../components/ReelModal";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "all",        label: "All",       match: () => true },
  { key: "forest",     label: "Forest",    match: (e) => /forest|ghats|nallamala|maredumilli/i.test(e.region + e.location) },
  { key: "beach",      label: "Coast",     match: (e) => /island|coast|goa|andaman|honnavar/i.test(e.region + e.location) },
  { key: "mountains",  label: "Mountains", match: (e) => /himalaya|manali/i.test(e.region + e.location) },
];

export default function Episodes() {
  const [episodes, setEpisodes] = useState(FALLBACK_EPISODES);
  const [openEp, setOpenEp] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/episodes`).then((r) => setEpisodes(r.data)).catch(() => {});
  }, []);

  const filtered = episodes.filter(FILTERS.find((f) => f.key === filter).match);

  return (
    <main data-testid="episodes-page" className="relative pt-24" style={{ background: "var(--coal)", minHeight: "100vh" }}>
      {/* HEADER */}
      <section className="relative px-5 md:px-16 py-16 md:py-24 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(201,160,74,.06) 0%, transparent 60%)",
          }}
        />
        <Reveal className="relative z-[2] text-center max-w-3xl mx-auto">
          <span className="st-eyebrow">All Episodes · Season I</span>
          <h1 className="mt-3 st-cinzel font-black text-[var(--cream)] leading-[0.92]" style={{ fontSize: "clamp(2.4rem, 7vw, 6rem)" }}>
            THE FULL
            <em className="block not-italic st-cormorant italic font-light text-[var(--gold-lt)]" style={{ fontSize: "clamp(1.6rem, 5vw, 4rem)", marginTop: 6 }}>
              Catalogue.
            </em>
          </h1>
          <div className="st-rule mx-auto mt-5"/>
          <p className="mt-5 st-infant text-base md:text-lg text-[var(--fog)]">
            Six destinations. Six conversations. Six versions of being alive.
          </p>
          <a
            data-testid="episodes-header-etvwin"
            href={ETV_WIN_SERIES_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 st-btn st-interactive"
            style={{
              background: "#e21e26",
              borderColor: "#e21e26",
              color: "#ffffff",
              boxShadow: "0 8px 28px rgba(226,30,38,.3)",
            }}
          >
            <Play size={11} fill="currentColor"/> Watch Full Episodes on ETV Win <ExternalLink size={11}/>
          </a>
        </Reveal>

        {/* FILTERS */}
        <Reveal delay={120} className="relative z-[2] mt-12 flex flex-wrap items-center justify-center gap-2 md:gap-3">
          <Filter size={13} className="text-[var(--gold)] mr-1"/>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className="st-cinzel text-[0.52rem] tracking-[0.32em] uppercase px-4 py-2 transition-colors st-interactive"
              style={{
                border: "1px solid rgba(201,160,74,.35)",
                background: filter === f.key ? "var(--gold)" : "transparent",
                color: filter === f.key ? "var(--ink)" : "var(--gold)",
              }}
            >
              {f.label}
            </button>
          ))}
        </Reveal>
      </section>

      {/* GRID */}
      <section className="relative px-5 md:px-16 pb-32">
        <div
          data-testid="episodes-grid"
          className="grid gap-6 md:gap-8"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}
        >
          {filtered.map((ep, i) => (
            <EpisodeReel key={ep.id} episode={ep} index={i} onOpen={(e) => setOpenEp(e)} />
          ))}
        </div>
        {filtered.length === 0 && (
          <div data-testid="episodes-empty" className="text-center py-20 st-infant text-[var(--fog)]">
            No episodes match that filter — try another.
          </div>
        )}
      </section>

      {openEp && <ReelModal episode={openEp} onClose={() => setOpenEp(null)} />}
    </main>
  );
}
